import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client'; // <-- named import for v4
import { API_BASE_URL,SOCKET_API_BASE_URL } from '../constant';

// Compute socket origin from the env var you already use to build REST URLs.
// e.g. REACT_APP_API_BASE_URL = "https://api.sappsapp.com/api"
const getSocketServerUrl = () => {
  const url = new URL(SOCKET_API_BASE_URL);
  // Strip any REST prefix like /api
  url.pathname = '';
  return url.origin; // "https://api.sappsapp.com"
};
const SOCKET_SERVER_URL = getSocketServerUrl();

// IMPORTANT: set this to match your server/proxy!
// If your server serves the default Socket.IO route, keep "/socket.io".
// If your reverse proxy mounts it under /api, use "/api/socket.io".
const SOCKET_PATH = '/socket.io';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children, isAuthenticated }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up if user logs out
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Initialize socket connection when user is authenticated
    const token = localStorage.getItem('token');

    const socketInstance = io(SOCKET_SERVER_URL, {
      path: SOCKET_PATH,
      auth: { token },                // server should read socket.handshake.auth.token
      withCredentials: true,          // only matters if you use cookie-based auth
      transports: ['websocket'], // start with polling then upgrade
      // transports: ['polling', 'websocket'], // start with polling then upgrade
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
    });

    // Helpful low-level engine logs (in dev only)
    socketInstance.io.engine.on('upgrade', (t) =>
      console.log('[socket] transport upgrade ->', t.name)
    );
    socketInstance.io.engine.on('close', (reason) =>
      console.log('[socket] engine close:', reason)
    );

    // Connection event handlers
    socketInstance.on('connect', () => {
      console.log('Socket connected:', socketInstance.id);
      setIsConnected(true);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      console.error('Attempting to connect to:', SOCKET_SERVER_URL);
      console.error('API_BASE_URL used:', API_BASE_URL);
      console.error('Error details:', {
        message: error.message,
        description: error.description,
        context: error.context,
        type: error.type
      });
      setIsConnected(false);
    });

    socketInstance.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error);
    });

    socketInstance.on('reconnect_failed', () => {
      console.error('Socket reconnection failed after maximum attempts');
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const value = {
    socket,
    isConnected,
    emit: (event, data) => { socket?.emit(event, data); },
    on: (event, callback) => {
      if (!socket) return () => {};
      socket.on(event, callback);
      return () => socket.off(event, callback);
    },
    off: (event, callback) => { socket?.off(event, callback); },
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
